import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { api, type FeedBlockWire, type FeedCollection, type FeedTileWire } from "@/api";
import { EmptyState, ErrorState } from "@/components/common/states";
import { Plus16Icon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { CategoryRow, CollectionMosaic, HeroCampaign, HomeGenderTabs, IdeaRail, PhotoBanner, ProductRail } from "@/components/feed/home-sections";
import { ProductCard } from "@/components/product/product-card";
import { ProductGrid, ProductGridSkeleton } from "@/components/product/product-grid";
import { type AdItem, useAdPlacements } from "@/lib/ad-tracking";
import { Skeleton } from "@osmix/ui/skeleton";
import { buildHomeGridSegments, DEFAULT_HOME_SEGMENT, feedDepartmentOf, resolveRailProducts, splitHomeFeedBlocks } from "@/lib/home-feed";
import { DiscoverySourceProvider } from "@/lib/discovery-source";
import { resolveCategoryId } from "@/lib/category-tree";
import { useArchivedCategories, useCategoryTree } from "@/lib/use-category-tree";
import { cn } from "@/lib/utils";
import { useDepartment, useFeedFilters, useSegment } from "@/store";
import { useVisibleIdeas } from "@/store/hidden-ideas";
import { useProfile } from "@/store/profile";
import { useWishlist } from "@/store/wishlist";
import { usePreferences } from "@/store/preferences";
import { type FeedFilters, FeedSettingsPanel, feedQueryOf, hasFeedFilters } from "@/components/feed/feed-settings-panel";
import { CollectionCard } from "./feed";
import { InstallAppPrompt } from "@/components/pwa/install-prompt";
import { usePageMeta } from "@/lib/use-page-meta";
import { HotPromos } from "@/components/promo/hot-promos";

// Stable keys for the fixed-length loading grid (index-as-key is a lint error).
const WALL_SKELETON_KEYS = ["a", "b", "c", "d", "e", "f", "g", "h"];

// The buyer home ("/"). It consumes the gateway's single managed feed
// (GET /feed/blocks) and renders the ordered blocks top-to-bottom, mirroring
// the mobile app's Home one-to-one:
//   • gender/segment tabs at the top switch the audience → refetch by segment;
//   • `personalFeed` is a MARKER — blocks before it are the header above the
//     grid, blocks after it are merchandising interleaved into the grid;
//   • productRail carries only ids, resolved through the catalog;
//   • «Моя лента» is the product grid filtered by the segment's root category.
// If the feed is empty, home falls back to the inspiration collections wall so
// it is never blank.
export function HomePage() {
  const {
    t
  } = useTranslation();
  usePageMeta();
  // Сегмент общий для всех страниц витрины (главная, идеи, подборки) — иначе
  // покупатель, выбравший «Мужчинам», на странице идей снова видел женские.
  const segment = useSegment();
  const setSegment = usePreferences(s => s.setSegment);
  // The header department («Одежда» / «Дом») re-roots the product spine below.
  const department = useDepartment();
  // Корни раздела — из дерева категорий API («Одежда» = все аудитории).
  const tree = useCategoryTree(department);
  const isClothes = tree.isClothes;
  // Регион покупателя → ассортимент ленты под город.
  const city = usePreferences(s_0 => s_0.city);
  // «Продвигаемые товары» (этап 8, П1): кампании пакета «Максимальный охват».
  const promoted = useAdPlacements({
    place: "home",
    city,
    limit: 8
  });
  const blocksQuery = useQuery({
    queryKey: ["feed", "blocks", department, segment],
    queryFn: () => api.feed.blocks(segment, feedDepartmentOf(department))
  });
  // Backs both the ideaRail fallback (when the feed has no such block) and the
  // whole-page fallback (when the feed is empty).
  const collectionsQuery = useQuery({
    queryKey: ["feed", "collections"],
    queryFn: () => api.feed.collections()
  });
  const blocks = useMemo(() => blocksQuery.data ?? [], [blocksQuery.data]);
  const {
    header,
    injected,
    personal
  } = useMemo(() => splitHomeFeedBlocks(blocks), [blocks]);

  // «Моя лента» (Лента OSMIX) НЕ зависит от вкладки аудитории: переключение
  // Женщинам/Мужчинам/Детям меняет редакционные блоки (идеи/категории/подборки),
  // а лента остаётся как есть. Поэтому грид рутится только по разделу: корни
  // раздела из дерева категорий («Одежда» — все аудитории сразу). Ключ без segment,
  // чтобы лента не перезапрашивалась при смене таба.
  // «Настроить» собирает ленту покупателя: категории, размеры, цвета, цену и
  // наличие. Выбранные категории заменяют корни отдела — иначе фильтр по
  // «Платьям» всё равно тянул бы весь гардероб.
  const feedFilters = useFeedFilters();
  const setFeedFilters = usePreferences(s_1 => s_1.setFeedFilters);
  const [feedSettingsOpen, setFeedSettingsOpen] = useState(false);
  // Сохранённые категории ленты могут указывать на узел, ушедший в архив
  // (дерево v1: cat-3, cat-8…): такие ведём на преемника из шлюза (пока ручка
  // не ответила — по запасной таблице), без преемника — выбрасываем. Итог — в
  // ключе запроса: ответ ручки перезапрашивает ленту, только если он что-то
  // поменял.
  const archivedTree = useArchivedCategories();
  const savedCategoryIds = useMemo(() => tree.isPending ? feedFilters.categoryIds : [...new Set(feedFilters.categoryIds.map(id => resolveCategoryId(tree.categories, id, segment, archivedTree.archived)).filter((id_0): id_0 is string => id_0 != null))], [tree.isPending, tree.categories, feedFilters.categoryIds, segment, archivedTree.archived]);
  // Запрос ленты под набор фильтров — один на сетку и на счётчик панели
  // «Показать N товаров».
  const feedQuery = (filters: FeedFilters, categoryIds: string[]) => ({
    ...feedQueryOf(filters),
    categoryIds: categoryIds.length > 0 ? categoryIds : tree.departmentRootIds,
    pageSize: 48,
    city
  });
  // Персональная лента (п.16 правок 27.10): тот же набор товаров, что и
  // раньше (раздел + «Настроить» — жёсткие фильтры), но порядок — по профилю
  // покупателя (покупки, корзина, избранное, просмотры). Seed прохода живёт,
  // пока открыта главная: с ним страницы не повторяют товары, а повторный
  // запрос страницы (ретрай, возврат на вкладку) отдаёт ровно её же. Вход и
  // выход меняют ключ — лента пересобирается под нового зрителя.
  const [feedSeed] = useState(() => Math.random().toString(36).slice(2, 12));
  const viewerId = useProfile(s_2 => s_2.user?.id ?? null);
  const guestClientId = useWishlist(s_3 => s_3.clientId);
  const gridQuery = useInfiniteQuery({
    queryKey: ["products", "home", "for-you", feedSeed, viewerId, department, city, feedFilters, savedCategoryIds, tree.departmentRootIds],
    enabled: feedFilters.categoryIds.length > 0 || !tree.isPending,
    initialPageParam: null as string | null,
    queryFn: ({
      pageParam
    }) => api.feed.forYou({
      ...feedQuery(feedFilters, savedCategoryIds),
      seed: feedSeed,
      cursor: pageParam,
      // И вошедшему: шлюз добавит к профилю его гостевые события до входа.
      clientId: guestClientId
    }),
    getNextPageParam: last => last.meta.nextCursor ?? undefined
  });
  // Плитки ленты — по вариантам-цветам под те же условия (A-09).
  const feedTileFilter = {
    colors: feedFilters.colors,
    priceMin: feedFilters.priceMin,
    priceMax: feedFilters.priceMax,
    sale: feedFilters.sale,
    inStockOnly: feedQueryOf(feedFilters).inStock
  };
  // Макет показывает каждую карточку ленты с фото. Демо-каталог содержит
  // импортированные позиции без изображений — прячем их из ленты покупателя,
  // чтобы не было «стены» пустых плиток (соответствие Figma «Лента OSMIX»).
  const gridProducts = useMemo(() => (gridQuery.data?.pages ?? []).flatMap(page => page.data).filter(p => p.photos.length > 0), [gridQuery.data]);

  // productRail blocks carry only ids — resolve them all in one batch through
  // the catalog (the app resolves from a product pool for the same reason).
  const railIds = useMemo(() => Array.from(new Set(blocks.filter(b => b.kind === "productRail").flatMap(b_0 => b_0.productIds ?? []))), [blocks]);
  const railQuery = useQuery({
    queryKey: ["products", "by-ids", railIds],
    queryFn: () => api.products.byIds(railIds),
    enabled: railIds.length > 0
  });
  const railById = useMemo(() => new Map((railQuery.data ?? []).map(p_0 => [p_0.id, p_0] as const)), [railQuery.data]);

  // «Скрыть идею» (A-26): скрытая до загрузки страницы идея пропадает и с
  // главной — из рельса «Идеи OSMIX», мозаик и стены. Скрытые в этой сессии
  // остаются на месте до обновления, как на стене идей (useVisibleIdeas).
  const visibleIdeas = useVisibleIdeas(collectionsQuery.data);
  const hiddenIdeaRefs = (() => {
    const shown = new Set(visibleIdeas.ideas.map(c => c.id));
    const refs = new Set<string>();
    for (const c_0 of collectionsQuery.data ?? []) {
      if (shown.has(c_0.id)) continue;
      refs.add(c_0.id);
      if (c_0.slug) refs.add(c_0.slug);
    }
    return refs;
  })();
  // Плитки и слайды блоков фида, ведущие на скрытую идею, не рисуем.
  const withoutHidden = <T extends {
    collectionSlug?: string;
  },>(list: T[] | undefined): T[] => (list ?? []).filter(t_0 => !t_0.collectionSlug || !hiddenIdeaRefs.has(t_0.collectionSlug));

  // Collections mapped into idea tiles, for the ideaRail fallback.
  const collectionTiles: FeedTileWire[] = visibleIdeas.ideas.map(c_1 => ({
    id: `col-${c_1.id}`,
    imageUrl: c_1.cover,
    label: c_1.title,
    collectionSlug: c_1.slug
  }));
  // Мозаике нужно пять плиток: если покупатель скрыл одну из них, добираем
  // видимыми идеями, а не прячем весь блок.
  const mosaicTiles = (items: FeedTileWire[] | undefined): FeedTileWire[] => {
    const kept = withoutHidden(items);
    if (kept.length >= 5 || kept.length === (items ?? []).length) return kept;
    const used = new Set(kept.map(t_1 => t_1.collectionSlug).filter(Boolean));
    return [...kept, ...collectionTiles.filter(t_2 => !used.has(t_2.collectionSlug))].slice(0, 5);
  };
  const heroBlock = useMemo(() => header.find(b_1 => b_1.kind === "campaign"), [header]);
  const restHeader = useMemo(() => header.filter(b_2 => b_2 !== heroBlock), [header, heroBlock]);
  const hasIdeaRail = useMemo(() => header.some(b_3 => b_3.kind === "ideaRail"), [header]);
  const gridSegments = useMemo(() => buildHomeGridSegments(gridProducts, injected), [gridProducts, injected]);

  // One renderer for a block wherever it sits (header row or grid injection),
  // so header and interleaved placements can never drift apart. Unknown kinds
  // draw nothing — the gateway can add block types without blanking home.
  const renderBlock = (block: FeedBlockWire) => {
    switch (block.kind) {
      case "campaign":
        return <HeroCampaign key={block.id} slides={withoutHidden(block.slides)} />;
      case "categoryRow":
        return <CategoryRow key={block.id} title={block.title} description={block.description} items={block.items ?? []} />;
      case "ideaRail":
        return <IdeaRail key={block.id} title={block.title} description={block.description}
        // Keep the inspiration wall even if the block ships empty.
        items={block.items?.length ? withoutHidden(block.items) : collectionTiles} />;
      case "collectionMosaic":
        return <CollectionMosaic key={block.id} title={block.title} description={block.description} items={mosaicTiles(block.items)} />;
      case "photoBanner":
        return <PhotoBanner key={block.id} title={block.title} description={block.description} slides={withoutHidden(block.slides)} />;
      case "productRail":
        return <ProductRail key={block.id} title={block.title} description={block.description} products={resolveRailProducts(block.productIds, railById)} />;
      default:
        return null;
    }
  };
  const feedEmpty = !blocksQuery.isPending && blocks.length === 0;
  return (
    // Главная целиком — управляемая выдача: «Лента OSMIX», рельсы подборок,
    // идеи. Это не полка каталога и не ответ на запрос покупателя, поэтому
    // товар, найденный здесь, продавец видит как «Рекомендации».
    <DiscoverySourceProvider value="recommendations">
		{/* Ритм макета: от низа блока до заголовка следующего — 50 (Desktop: gap 20
      + отступы блоков 10+10 + отступ заголовка 10), 30 (Tablet), 20 (Mobile). */}
		<div className="flex flex-col gap-5 sm:gap-[30px] lg:gap-[50px]">
			<InstallAppPrompt placement="home" />
			{blocksQuery.isPending ? <div className="flex flex-col gap-10">
					{/* Full-bleed hero skeleton: escapes the main padding like the hero. */}
					<Skeleton className="-mx-4 -mt-2.5 aspect-[420/352] w-auto rounded-none sm:aspect-[744/418] md:-mx-8 lg:aspect-[1200/674]" />
					<ProductGridSkeleton />
				</div> : feedEmpty ? <>
					{/* Гендерные вкладки — только для «Одежды»; «Дом» без гендера. */}
					{isClothes && <HomeGenderTabs active={segment} onSelect={setSegment} />}
					<HotPromos />
					<InspirationWall pending={collectionsQuery.isPending} error={collectionsQuery.error != null} collections={visibleIdeas.ideas} onRetry={() => collectionsQuery.refetch()} />
				</> : <>
					{/* Full-bleed hero, flush to the header and container edges. */}
					{/* После баннера: табы аудиторий через 10px, «Дом» (без табов) — через 20px
         (поправка на зазор колонки 20/30/50). */}
					{heroBlock?.slides?.length ? <div data-header-hero className={cn("-mx-4 -mt-2.5 md:-mx-8", isClothes ? "-mb-2.5 sm:-mb-5 lg:-mb-10" : "sm:-mb-2.5 lg:-mb-[30px]")}>
							<HeroCampaign slides={withoutHidden(heroBlock.slides)} bleed />
						</div> : null}

					{/* Гендерные вкладки — только для «Одежды»; «Дом» без гендера. */}
					{/* От табов до заголовка «Идеи OSMIX»: 40 / 20 / 10 — на 10 меньше зазора колонки. */}
					{isClothes && <div className="-mb-2.5">
							<HomeGenderTabs active={segment} onSelect={setSegment} />
						</div>}

					{/* «Горящие акции» (П3, fd-promo-10): сразу под вкладками — это
         блок повышенной видимости. Нет горящих акций — блока нет. */}
					<HotPromos />

					{/* ideaRail is header canon; synthesise one from collections if absent.
         По макету «Идеи OSMIX» идут сразу под табами — выше «Категорий»
         и «Подборок» (restHeader), поэтому рендерим до них. Коллекции —
         одёжные, поэтому fallback только в «Одежде»; в «Доме» показываем
         ideaRail лишь если он пришёл из фида. */}
					{isClothes && !hasIdeaRail && collectionTiles.length > 0 && <IdeaRail title="Идеи OSMIX" description="Вдохновение на красоту" items={collectionTiles} />}

					{restHeader.map(renderBlock)}

					<PromotedRail items={promoted.data ?? []} title={t("ads.promotedTitle")} />

					{/* personalFeed marker → «Лента OSMIX» header + the interleaved grid. */}
					{/* Шапка «Лента OSMIX» и сетка — через 10px (кадр «Лента», tabs pad 10). */}
					<section className="flex flex-col gap-2.5">
						<div className="flex items-center justify-between gap-4">
							{/* Desktop/Tablet 600/20/23 #1F1F1F, Mobile 400/16/20 #161616 — как у заголовков блоков. */}
							<h2 className="text-[16px] font-normal leading-5 text-[#161616] sm:text-[20px] sm:font-semibold sm:leading-[23px] sm:text-[#1F1F1F]">
								{personal?.title ?? "Лента OSMIX"}
							</h2>
							{/* «Настроить» → та же панель фильтров, что в каталоге (A-11). Кнопка по
           макету — DS accent: Mobile 32 r8 pad 12, 600/12; Tablet/Desktop 40 r10
           pad 16, 600/14; иконка plus 16, зазор 10. */}
							<Button variant="accent" size="sm" onClick={() => setFeedSettingsOpen(true)} className="gap-2.5 sm:h-10 sm:rounded-[10px] sm:px-4 sm:text-[14px] sm:leading-[18px]">
								<Plus16Icon />
								{hasFeedFilters(feedFilters) ? "Фильтры ленты" : "Настроить"}
							</Button>
						</div>
						{gridQuery.isPending ? <ProductGridSkeleton /> : gridQuery.error ? <ErrorState onRetry={() => gridQuery.refetch()} /> : gridProducts.length === 0 && injected.length === 0 ? <EmptyState icon={Sparkles} title={hasFeedFilters(feedFilters) ? "Под ваши фильтры ничего не нашлось" : "Пока пусто"} hint={hasFeedFilters(feedFilters) ? "Смягчите условия в настройке ленты — например, снимите размер или цену." : "Загляните позже — здесь появятся товары этого раздела."} /> : <div className="flex flex-col gap-5 sm:gap-[30px] lg:gap-[50px]">
								{gridSegments.map(seg => seg.type === "grid" ? <ProductGrid key={seg.key} products={seg.products} tileFilter={feedTileFilter} /> : <div key={seg.key}>{renderBlock(seg.block)}</div>)}
								{/* Лента длиннее одной страницы — догружаем по кнопке: под лентой
            стоит мозаика «Подборки», бесконечная прокрутка её бы съела. */}
								{gridQuery.hasNextPage && <div className="flex justify-center">
										<Button variant="outline" onClick={() => gridQuery.fetchNextPage()} disabled={gridQuery.isFetchingNextPage} data-testid="home-feed-more">
											{gridQuery.isFetchingNextPage ? "Загружаем…" : "Показать ещё"}
										</Button>
									</div>}
							</div>}
					</section>

					{/* Настройка «Моей ленты» (A-11): та же панель фильтров, что в каталоге, слева. */}
					<FeedSettingsPanel open={feedSettingsOpen} onOpenChange={setFeedSettingsOpen} value={feedFilters} countQuery={draft => feedQuery(draft, draft.categoryIds)} onApply={next => {
            setFeedFilters(next);
            toast.success(t("feedSettings.saved", {
              defaultValue: "Лента настроена"
            }));
          }} />

					{/* «в самом конце всех товаров — подборка»: a collection mosaic at the
         feed tail. Stands down when there are no collections → just the
         feed, per «если коллекций нет — просто лента». */}
					{collectionTiles.length > 0 && <CollectionMosaic title="Подборки" description="Уникальные товары от мастеров" items={collectionTiles} />}
				</>}
		</div>
		</DiscoverySourceProvider>
  );
}

// The inspiration collections wall — the home's fallback when the managed feed
// is empty, reusing the same masonry card as the standalone feed page.
function InspirationWall(t0) {
  const $ = _c(8);
  const {
    pending,
    error,
    collections,
    onRetry
  } = t0;
  if (pending) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 xl:columns-4">{WALL_SKELETON_KEYS.map(_temp18)}</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (error) {
    let t1;
    if ($[1] !== onRetry) {
      t1 = <ErrorState onRetry={onRetry} />;
      $[1] = onRetry;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (collections.length === 0) {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <EmptyState icon={Sparkles} title={"\u041F\u043E\u043A\u0430 \u043F\u0443\u0441\u0442\u043E"} hint={"\u0417\u0430\u0433\u043B\u044F\u043D\u0438\u0442\u0435 \u043F\u043E\u0437\u0436\u0435 \u2014 \u043F\u043E\u0434\u0431\u043E\u0440\u043A\u0438 \u043F\u043E\u044F\u0432\u044F\u0442\u0441\u044F, \u043A\u0430\u043A \u0442\u043E\u043B\u044C\u043A\u043E \u043C\u0430\u0441\u0442\u0435\u0440\u0430 \u0434\u043E\u0431\u0430\u0432\u044F\u0442 \u0442\u043E\u0432\u0430\u0440\u044B."} />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== collections) {
    t1 = collections.map(_temp19);
    $[4] = collections;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 xl:columns-4">{t1}</div>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// «Продвигаемые товары» на главной (этап 8, П1, fd-promo-27): товары
// кампаний «Максимальный охват» рельсом, как подборки, с меткой «Реклама».
// Нет рекламы — блока нет.
function _temp19(c) {
  return <CollectionCard key={c.id} collection={c} />;
}
function _temp18(key, i) {
  return <Skeleton key={key} className="mb-4 w-full rounded-[4px]" style={{
    height: 240 + i % 3 * 80
  }} />;
}
function PromotedRail(t0) {
  const $ = _c(9);
  const {
    items,
    title
  } = t0;
  if (items.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== title) {
    t1 = <h2 className="text-[16px] font-normal leading-5 text-[#1F1F1F] sm:text-[20px] sm:font-semibold sm:leading-[23px]">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== items) {
    t2 = items.map(_temp20);
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="-mx-4 flex snap-x scroll-px-4 gap-2 overflow-x-auto px-4 pb-1 sm:gap-2.5 md:mx-0 md:scroll-px-0 md:px-0 lg:gap-5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <DiscoverySourceProvider value="ads"><section className="flex flex-col gap-2.5" data-testid="home-promoted">{t1}{t3}</section></DiscoverySourceProvider>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp20(item) {
  return <div key={`${item.campaignId}:${item.productId}`} className="w-[166px] shrink-0 snap-start sm:w-[173px]"><ProductCard product={item.product} variant="rail" ad={{
      campaignId: item.campaignId,
      placement: "home"
    }} /></div>;
}